import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Mail, ArrowLeft, Edit2 } from 'lucide-react';
import Button from '../components/common/Button';
import Alert from '../components/common/Alert';
import Input from '../components/common/Input';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://fabio-ubuntu:8082/api';

const EmailVerification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Buscar email de múltiplas fontes (prioridade: state -> localStorage -> vazio)
  const emailFromState = location.state?.email || '';
  const emailFromStorage = localStorage.getItem('pendingEmailVerification') || '';
  const initialEmail = emailFromState || emailFromStorage;

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [email, setEmail] = useState(initialEmail);
  const [isEditingEmail, setIsEditingEmail] = useState(!initialEmail); // Se não houver email, já começa editando
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    setError('');
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...code];

    pastedData.split('').forEach((char, index) => {
      if (index < 6) {
        newCode[index] = char;
      }
    });

    setCode(newCode);
    inputRefs.current[Math.min(pastedData.length, 5)]?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const codeString = code.join('');
    if (codeString.length !== 6) {
      setError('Por favor, digite o código completo de 6 dígitos');
      return;
    }

    if (!email || !email.trim()) {
      setError('Por favor, informe seu email');
      setIsEditingEmail(true);
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/associados/verify-email`, {
        email: email.trim(),
        code: codeString,
      });

      // Limpar localStorage após verificação bem-sucedida
      localStorage.removeItem('pendingEmailVerification');

      setSuccess(true);

      setTimeout(() => {
        navigate('/associado/login');
      }, 3000);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          'Código inválido ou expirado. Por favor, tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email || !email.trim()) {
      setError('Por favor, informe seu email');
      setIsEditingEmail(true);
      return;
    }

    setResending(true);
    setResendMessage('');
    setError('');

    try {
      await axios.post(`${API_URL}/associados/resend-code`, { email: email.trim() });
      setResendMessage('Código reenviado com sucesso! Verifique seu e-mail.');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao reenviar código');
    } finally {
      setResending(false);
    }
  };

  const handleSaveEmail = () => {
    if (!email || !email.trim()) {
      setError('Por favor, informe um email válido');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Por favor, informe um email válido');
      return;
    }

    setIsEditingEmail(false);
    setError('');
    // Atualizar localStorage com o novo email
    localStorage.setItem('pendingEmailVerification', email.trim());
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-green-50 py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="mx-auto h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mb-6 animate-pulse">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              E-mail Verificado com Sucesso!
            </h2>
            <p className="text-gray-600 mb-4">
              Seu cadastro foi encaminhado para análise do administrador.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Você receberá um e-mail assim que seu cadastro for aprovado.
            </p>
            <p className="text-sm text-gray-400">
              Redirecionando para login...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-green-50 py-12 px-4">
      <div className="max-w-md w-full">
        <button
          onClick={() => navigate('/')}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Voltar</span>
        </button>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="mx-auto h-16 w-16 bg-gradient-to-r from-green-500 to-green-700 rounded-xl flex items-center justify-center mb-4 shadow-lg">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">Verificar E-mail</h2>
            <p className="mt-2 text-sm text-gray-600">
              Digite o código de 6 dígitos enviado para
            </p>

            {/* Campo de email editável ou fixo */}
            {isEditingEmail ? (
              <div className="mt-4 max-w-sm mx-auto">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  className="text-center"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveEmail();
                    }
                  }}
                />
                <Button
                  onClick={handleSaveEmail}
                  className="mt-2 w-full bg-green-600 hover:bg-green-700"
                  size="sm"
                >
                  Confirmar Email
                </Button>
              </div>
            ) : (
              <div className="mt-2 flex items-center justify-center gap-2">
                <p className="text-sm font-semibold text-green-600">{email}</p>
                <button
                  onClick={() => setIsEditingEmail(true)}
                  className="text-gray-400 hover:text-green-600 transition-colors"
                  title="Editar email"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {error && (
            <Alert variant="error" onClose={() => setError('')} className="mb-6">
              {error}
            </Alert>
          )}

          {resendMessage && (
            <Alert variant="success" onClose={() => setResendMessage('')} className="mb-6">
              {resendMessage}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex justify-center gap-2" onPaste={handlePaste}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { inputRefs.current[index] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
                  disabled={loading}
                />
              ))}
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800"
              loading={loading}
              disabled={loading || code.some((d) => !d)}
            >
              {loading ? 'Verificando...' : 'Verificar Código'}
            </Button>

            <div className="text-center space-y-2">
              <p className="text-sm text-gray-600">Não recebeu o código?</p>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resending}
                className="text-sm text-green-600 hover:text-green-700 font-medium disabled:opacity-50"
              >
                {resending ? 'Reenviando...' : 'Reenviar código'}
              </button>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-800 text-center">
                <strong>Dica:</strong> O código é válido por 24 horas. Após a verificação, aguarde a aprovação do administrador.
              </p>
              {emailFromStorage && !emailFromState && (
                <p className="text-xs text-blue-800 text-center mt-2">
                  Você pode acessar esta página novamente a qualquer momento até concluir a verificação.
                </p>
              )}
            </div>
          </form>
        </div>

        <p className="mt-4 text-center text-xs text-gray-500">
          Cartão HMG - 2025
        </p>
      </div>
    </div>
  );
};

export default EmailVerification;
